// src/components/layout/Sidebar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Activity, User, Grid3x3, ChevronLeft, ChevronRight, LogOut, Brain, MessageCircle, MessageSquare, Upload, Pill, Camera, Sparkles } from 'lucide-react';
import { FeedbackModal } from '@/components/dashboard/FeedbackModal';
import { UploadModal } from '@/components/dashboard/UploadModal';

type DashboardView = 'health' | 'upload' | 'profile' | 'category-detail' | 'categories' | 'brain' | 'chat' | 'prescription';

interface SidebarProps {
  currentView: DashboardView;
  userEmail: string;
  onNavigate: (view: DashboardView) => void;
  onLogout: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
  onShowPreview?: (data: any, jobId: string) => void;
  onUploadComplete?: () => void;
  hasData?: boolean;
  isDemo?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  userEmail,
  onNavigate,
  onLogout,
  isOpen,
  onToggle,
  onCollapsedChange,
  onShowPreview,
  onUploadComplete,
  hasData = true,
  isDemo = false
}) => {
  // On mobile, use parent state (isOpen prop)
  // On desktop, use internal collapsed state
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [isCollapsed, setIsCollapsed] = useState(!isMobile);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // For mobile, use isOpen prop; for desktop, use inverse of isCollapsed
  const isVisible = isMobile ? (isOpen ?? false) : !isCollapsed;

  const navigationItems = [
    {
      id: 'health' as DashboardView,
      label: 'Health Dashboard',
      icon: Activity,
      activeViews: ['health'] as DashboardView[]
    },
    {
      id: 'categories' as DashboardView,
      label: 'Categories',
      icon: Grid3x3,
      activeViews: ['categories', 'category-detail'] as DashboardView[]
    },
    {
      id: 'prescription' as DashboardView,
      label: 'Prescription',
      icon: Pill,
      activeViews: ['prescription'] as DashboardView[]
    },
    {
      id: 'upload' as DashboardView,
      label: 'Upload Report',
      icon: Upload,
      activeViews: ['upload'] as DashboardView[],
      isUploadButton: true // Special flag to handle differently
    },
    {
      id: 'brain' as DashboardView,
      label: 'My Brain',
      icon: Brain,
      activeViews: ['brain'] as DashboardView[]
    },
    {
      id: 'chat' as DashboardView,
      label: 'AI Chat',
      icon: MessageCircle,
      activeViews: ['chat'] as DashboardView[]
    },
    {
      id: 'profile' as DashboardView,
      label: 'Profile',
      icon: User,
      activeViews: ['profile'] as DashboardView[]
    }
  ];

  const isActive = (item: typeof navigationItems[0]) => {
    return item.activeViews.includes(currentView);
  };

  const handleToggle = () => {
    if (isMobile && onToggle) {
      onToggle();
    } else {
      const newCollapsed = !isCollapsed;
      setIsCollapsed(newCollapsed);
      onCollapsedChange?.(newCollapsed);
    }
  };

  // Notify parent of initial collapsed state
  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, []);

  // Toggle body class to prevent scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && isVisible) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [isMobile, isVisible]);

  // Update indicator position when currentView changes
  useEffect(() => {
    const updateIndicatorPosition = () => {
      // Find the active navigation item based on current view
      const activeItem = navigationItems.find(item => item.activeViews.includes(currentView));
      if (!activeItem || !indicatorRef.current) return;

      const activeButton = navItemRefs.current[activeItem.id];
      if (!activeButton) return;

      const buttonRect = activeButton.getBoundingClientRect();
      const navElement = activeButton.parentElement;
      if (!navElement) return;

      const navRect = navElement.getBoundingClientRect();
      const top = buttonRect.top - navRect.top;
      const height = buttonRect.height;
      const centerY = top + (height / 2);

      indicatorRef.current.style.top = `${centerY}px`;
      indicatorRef.current.style.transform = 'translateY(-50%)';
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateIndicatorPosition, 100);

    // Also update on window resize
    window.addEventListener('resize', updateIndicatorPosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateIndicatorPosition);
    };
  }, [currentView, isCollapsed]);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isVisible && (
        <div
          className="mobile-sidebar-overlay"
          onClick={handleToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          width: isCollapsed ? '70px' : '280px',
          backgroundColor: 'var(--color-surface)',
          borderRight: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease, transform 0.3s ease',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100dvh', // Use dynamic viewport height for better iOS support
          zIndex: 1000,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        className="sidebar"
        data-visible={isVisible}
      >
        {/* Toggle Button */}
        <button
          onClick={handleToggle}
          style={{
            position: 'absolute',
            top: '24px',
            right: '-12px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            transition: 'var(--transition-fast)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-primary)';
            e.currentTarget.style.borderColor = 'var(--color-primary)';
            const svg = e.currentTarget.querySelector('svg');
            if (svg) svg.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-surface)';
            e.currentTarget.style.borderColor = 'var(--color-border)';
            const svg = e.currentTarget.querySelector('svg');
            if (svg) svg.style.color = 'var(--color-secondary)';
          }}
        >
          {isCollapsed ? (
            <ChevronRight size={14} style={{ color: 'var(--color-secondary)' }} />
          ) : (
            <ChevronLeft size={14} style={{ color: 'var(--color-secondary)' }} />
          )}
        </button>

        {/* Logo/Brand */}
        <div
          style={{
            padding: isCollapsed ? 'var(--space-6) var(--space-3)' : 'var(--space-6) var(--space-4)',
            borderBottom: '1px solid var(--color-border)',
            textAlign: isCollapsed ? 'center' : 'left',
            transition: 'padding 0.3s ease'
          }}
        >
          <h1
            style={{
              fontFamily: 'var(--font-primary)',
              fontSize: isCollapsed ? 'var(--text-lg)' : 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-primary)',
              margin: 0,
              letterSpacing: 'var(--tracking-tight)',
              transition: 'font-size 0.3s ease'
            }}
          >
            {isCollapsed ? 'h' : 'heal'}
          </h1>
          {!isCollapsed && (
            <p
              style={{
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-muted)',
                margin: 'var(--space-1) 0 0 0'
              }}
            >
              Health AI Companion
            </p>
          )}
          {isDemo && !isCollapsed && (
            <div style={{
              marginTop: '12px',
              padding: '4px 10px',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '100px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              <Sparkles size={12} color="#3b82f6" />
              <span style={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#3b82f6',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>Demo Mode</span>
            </div>
          )}
          {isDemo && isCollapsed && (
            <Sparkles size={14} color="#3b82f6" style={{ marginTop: '8px' }} />
          )}
        </div>

        {/* Navigation Menu */}
        <nav
          style={{
            flex: 1,
            padding: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            position: 'relative'
          }}
        >
          {/* Tab Indicator */}
          <div
            ref={indicatorRef}
            className="tab-indicator"
            style={{
              position: 'absolute',
              left: '8px',
              width: '4px',
              height: '40px',
              background: 'var(--color-primary)',
              borderRadius: '4px',
              transition: 'all 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
              boxShadow: '0 0 12px rgba(0, 0, 0, 0.2)',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          />

          {navigationItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const isUpload = 'isUploadButton' in item && item.isUploadButton;

            return (
              <button
                key={item.id}
                ref={(el) => { navItemRefs.current[item.id] = el; }}
                onClick={() => {
                  if (isUpload) {
                    setIsUploadModalOpen(true);
                  } else {
                    onNavigate(item.id);
                  }
                }}
                title={isCollapsed ? item.label : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  gap: 'var(--space-3)',
                  padding: isCollapsed ? 'var(--space-3)' : 'var(--space-3) var(--space-4)',
                  backgroundColor: active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  border: isUpload && !hasData ? '2px solid #3b82f6' : 'none',
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-base)',
                  fontWeight: active ? 'var(--font-semibold)' : 'var(--font-medium)',
                  color: active ? 'var(--color-primary)' : 'var(--color-secondary)',
                  transition: 'var(--transition-fast)',
                  textAlign: 'left',
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  animation: isUpload && !hasData ? 'sidebar-pulse 2s infinite' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Icon size={20} />
                  {isCollapsed && (item.id === 'brain' || item.id === 'chat') && (
                    <span style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#F59E0B',
                      border: '2px solid var(--color-surface)',
                      boxShadow: '0 0 0 1px #F59E0B'
                    }} />
                  )}
                </div>
                {!isCollapsed && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <span>{item.label}</span>
                    {(item.id === 'brain' || item.id === 'chat') && (
                      <span style={{
                        fontSize: '9px',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#FEF3C7',
                        color: '#92400E',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        BETA
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Engineered in India Section */}
        {!isCollapsed && (
          <div
            style={{
              padding: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: 0.8,
                transition: 'opacity var(--transition-fast)',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-1)'
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-secondary)',
                    fontWeight: 'var(--font-medium)'
                  }}
                >
                  Engineered in
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-bold)',
                    color: 'var(--color-primary)',
                    textTransform: 'uppercase',
                    letterSpacing: 'var(--tracking-wide)'
                  }}
                >
                  India
                </span>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '1px',
                    overflow: 'hidden',
                    width: '15px',
                    height: '10px'
                  }}
                >
                  <div className="bg-india-saffron" style={{ height: '33.33%', width: '100%' }} />
                  <div
                    className="bg-india-white"
                    style={{
                      height: '33.33%',
                      width: '100%',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div
                      className="bg-india-blue"
                      style={{
                        width: '2px',
                        height: '2px',
                        borderRadius: '50%'
                      }}
                    />
                  </div>
                  <div className="bg-india-green" style={{ height: '33.33%', width: '100%' }} />
                </div>
              </div>
              <p
                style={{
                  fontSize: '10px',
                  color: 'var(--color-muted)',
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  margin: 0
                }}
              >
                Built for global health.
              </p>
            </div>
          </div>
        )}

        {/* User Info Footer */}
        <div
          style={{
            padding: 'var(--space-4)',
            borderTop: '1px solid var(--color-border)'
          }}
        >
          {!isCollapsed && (
            <p
              style={{
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-secondary)',
                margin: '0 0 var(--space-2) 0',
                wordBreak: 'break-word'
              }}
            >
              {userEmail}
            </p>
          )}

          {/* Send Feedback Button */}
          <button
            onClick={() => setIsFeedbackModalOpen(true)}
            title={isCollapsed ? 'Send Feedback' : undefined}
            style={{
              width: '100%',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-primary)',
              transition: 'var(--transition-fast)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)';
              e.currentTarget.style.borderColor = 'var(--color-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'var(--color-border)';
            }}
          >
            <MessageSquare size={16} />
            {!isCollapsed && <span>Send Feedback</span>}
          </button>

          {/* Logout Button */}
          <button
            onClick={onLogout}
            title={isCollapsed ? 'Logout' : undefined}
            style={{
              width: '100%',
              padding: 'var(--space-2) var(--space-3)',
              backgroundColor: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-error)',
              transition: 'var(--transition-fast)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.borderColor = 'var(--color-error)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'var(--color-border)';
            }}
          >
            <LogOut size={16} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside >

      {/* Feedback Modal */}
      < FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={onUploadComplete}
        onShowPreview={onShowPreview}
        onNavigateToPrescription={() => onNavigate('prescription')}
      />

      {/* Styles for responsive behavior and animations */}
      <style>{`
        @keyframes sidebar-pulse {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }

        /* Fallback for browsers without dvh support */
        @supports not (height: 100dvh) {
          .sidebar {
            height: 100vh !important;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            left: 0;
            top: 0;
            bottom: 0;
            width: 280px !important;
            height: 100dvh !important;
            height: 100vh !important; /* Fallback */
            z-index: 1000;
            transform: translateX(${isVisible ? '0' : '-100%'});
            transition: transform 0.3s ease;
            box-shadow: ${isVisible ? '2px 0 8px rgba(0,0,0,0.1)' : 'none'};
            -webkit-overflow-scrolling: touch;
          }

          .sidebar button[style*="right: -12px"] {
            display: none !important;
          }

          .mobile-sidebar-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 999;
            display: block;
          }

          /* Prevent body scroll when sidebar is open */
          body.sidebar-open {
            overflow: hidden;
            position: fixed;
            width: 100%;
          }
        }

        @media (min-width: 769px) {
          .mobile-sidebar-overlay {
            display: none;
          }
        }
      `}</style>
    </>
  );
};
